import { immerable } from 'immer'
import { isArray } from 'lodash-es'
import EventEmitter3 from 'eventemitter3'

import type { ElementIdentifier, Graph } from '..'
import {
  AbstractEdge,
  EVENT_NAME,
  EdgeData,
  LayoutEdgeData,
  EDGE_EVENT_NAME,
} from '../types'

export class Edge implements AbstractEdge {
  [immerable] = true

  id: ElementIdentifier
  graph: Graph
  eventEmitter: EventEmitter3
  source: AbstractEdge['source']
  target: AbstractEdge['target']
  label: AbstractEdge['label']
  edgeStyle: EdgeData['edgeStyle']
  renderKey = 0
  undirected = false
  states: string[] = []
  strokeWidth: NonNullable<EdgeData['strokeWidth']>
  color?: EdgeData['color']
  style?: EdgeData['style']
  element?: HTMLElement | SVGElement
  type?: AbstractEdge['type']
  data: EdgeData
  mergedEdges?: EdgeData[]

  constructor(graph: Graph, edgeData: EdgeData) {
    const { id, source, target, label, edgeStyle, strokeWidth, ...rest } =
      edgeData

    this.graph = graph
    this.eventEmitter = new EventEmitter3()
    this.source = source
    this.target = target
    this.label = label
    this.edgeStyle = edgeStyle
    this.strokeWidth = strokeWidth ?? 1
    this.id = id ?? `${source}-${target}-${label}`
    this.data = edgeData
    this.mergedEdges = edgeData.mergedEdges

    Object.assign(this, rest)
  }

  /**
   * 是否为边
   *
   * @param edge
   */
  static isEdge = (edge: ElementIdentifier | Edge): edge is Edge => {
    return edge instanceof Edge
  }

  /**
   * 起始节点
   */
  get sourceNode() {
    return this.graph.nodes.find((node) => node.id === this.source)
  }

  /**
   * 目标节点
   */
  get targetNode() {
    return this.graph.nodes.find((node) => node.id === this.target)
  }

  /**
   * 获取路径
   */
  getPath(): {
    d: string
    textPoint: {
      x: number
      y: number
    }
    textTransform: string
  } {
    throw new Error('not implemented')
  }

  /**
   * 设置边元素
   *
   * @param element
   */
  setElement(element: HTMLElement | SVGElement) {
    this.graph.service.send({
      type: 'SET_EDGE_ELEMENT',
      data: {
        id: this.id,
        element,
        eventName: EVENT_NAME['edge:mount'],
      },
    })
  }

  /**
   * 获取布局数据
   */
  toLayoutData(): LayoutEdgeData {
    const { source, target } = this

    return {
      source,
      target,
    }
  }

  /**
   * 设置状态列表
   *
   * @param states
   */
  setStates(states: string[]) {
    this.graph.service.send({
      type: 'UPDATE_EDGE_PROPERTIES',
      data: {
        id: this.id,
        edgeData: {
          states,
        },
      },
    })
  }

  /**
   * 设置单个状态
   *
   * @param state
   * @param enable
   */
  setState(state: string, enable = true) {
    if (enable) {
      if (!this.states.includes(state)) {
        this.setStates([...this.states, state])
      }
    } else {
      this.setStates(this.states.filter((val) => val !== state))
    }
  }

  /**
   * 取消状态
   *
   * @param states
   */
  clearStates(states?: string | string[]) {
    if (states) {
      states = isArray(states) ? states : [states]

      this.setStates(this.states.filter((state) => !states!.includes(state)))
    } else {
      this.setStates([])
    }
  }

  /**
   * 是否包含指定状态
   *
   * @param state
   * @returns
   */
  hasState(state: string) {
    return this.states.includes(state)
  }

  /**
   * 切换状态
   *
   * @param state
   */
  toggleState(state: string) {
    if (this.hasState(state)) {
      this.setStates(this.states.filter((val) => val !== state))
    } else {
      this.setStates([...this.states, state])
    }
  }

  /**
   * 独占某个状态
   *
   * @state 状态
   * @list 其他需要同状态的节点, 为空时则表示当前元素独占该状态
   */
  exclusiveState(state: string, list?: ElementIdentifier[]) {
    this.graph.service.send({
      type: 'SET_EDGE_EXCLUSIVE_STATE',
      data: {
        id: this.id,
        state,
        list,
      },
    })
  }

  /**
   * 派发位置更新事件
   */
  emitPositionUpdate = () => {
    this.eventEmitter.emit(EDGE_EVENT_NAME.onPositionUpdate)
  }

  /**
   * 更新事件
   */
  onPositionUpdate = (callback: () => void) => {
    this.eventEmitter.on(EDGE_EVENT_NAME.onPositionUpdate, callback)

    return () => {
      this.eventEmitter.removeListener(
        EDGE_EVENT_NAME.onPositionUpdate,
        callback
      )
    }
  }
}
